// AI-CONTEXT: See docs/features/livereport2-period-selector.md
//
// Client-side period filter for LiveReport2 widget data rows. Used for the
// template-blob path (rows are pre-fetched, never re-queried) and anywhere a
// Firestore range query would need a new composite index.
//
// Handles every timestamp shape the rows can carry: live Firestore Timestamp
// (.toDate()), serialized blob/JSON shapes ({ _seconds } / { seconds }),
// millisecond numbers, Date instances and parseable strings. Bounds are local
// days: start at 00:00:00.000, end at 23:59:59.999 (end date inclusive).

/** @param {string} iso - ISO date 'YYYY-MM-DD' */
const localStartOfDayMs = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d, 0, 0, 0, 0).getTime()
}

/** @param {string} iso - ISO date 'YYYY-MM-DD' */
const localEndOfDayMs = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
}

/**
 * @param {*} ts - A timestamp in any supported shape
 * @returns {number|null} Milliseconds since epoch, or null when unparseable
 */
const toMillis = (ts) => {
    if (ts == null) return null
    if (typeof ts?.toDate === 'function') return ts.toDate().getTime()
    if (typeof ts?._seconds === 'number') return ts._seconds * 1000
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000
    if (ts instanceof Date) return ts.getTime()
    if (typeof ts === 'number') return ts
    if (typeof ts === 'string') {
        const ms = new Date(ts).getTime()
        return isNaN(ms) ? null : ms
    }
    return null
}

/**
 * @param {Array<object>} rows - Data rows to filter
 * @param {{start: string|null, end: string|null}|null|undefined} range - ISO date bounds, null/empty = no filter
 * @param {(row: object) => *} [getTimestamp] - Reads the date off a row (defaults to row.Timestamp)
 * @returns {Array<object>} The filtered rows; the original array when no range is active
 */
const filterRowsByPeriod = (rows, range, getTimestamp = (row) => row?.Timestamp) => {
    const startMs = range?.start ? localStartOfDayMs(range.start) : null
    const endMs = range?.end ? localEndOfDayMs(range.end) : null

    if (startMs == null && endMs == null) return rows
    if (!Array.isArray(rows)) return []

    return rows.filter((row) => {
        const ms = toMillis(getTimestamp(row))
        if (ms == null || isNaN(ms)) return false
        if (startMs != null && ms < startMs) return false
        if (endMs != null && ms > endMs) return false
        return true
    })
}

export default filterRowsByPeriod
